// client/src/components/PointsImportModal.jsx
// Bulk point-credit from an Excel file (wave-12). The manager uploads a sheet
// where each row identifies a student (by card / national-id / external-id /
// system-id) plus a points amount and an optional reason. We preview the match
// server-side, then — in ONE click — credit every matched student their own
// points with their own reason.
//
// ALL-OR-NOTHING: if any row can't be matched to exactly one student, the
// commit is blocked and the problem rows are listed; the manager fixes the file
// and retries. A half-applied points run is worse than none.
//
// Server returns (analyze == commit shape):
//   { columns, summary:{ total, matched, problems, totalPoints, applied },
//     sample:[{ row, name, className, matchedBy, points, reason }],
//     problems:[{ row, identifier, errors[] }],
//     blocked, blockReason, dryRun }

import { useState } from 'react';
import Modal from './Modal.jsx';
import { usePointsImport } from '../hooks/useStudents.js';
import { useCamp } from '../hooks/useCamps.js';
import { formatNumber } from '../lib/currency.js';
import { ExclamationTriangleIcon, BanknotesIcon } from './Icons.jsx';

// The points-column rule depends on the camp currency: STAR camps take whole
// numbers only; SHEKEL/DOLLAR allow agorot (up to 2 decimal places).
const formatSpec = (isStar) => [
  {
    label: 'נקודות',
    required: true,
    note: isStar ? 'מספר שלם חיובי' : 'מספר חיובי (עד 2 ספרות אחרי הנקודה)',
  },
  { label: 'מזהה תלמיד', note: 'מספר כרטיס / תעודת זהות / מזהה חיצוני / מזהה מערכת — לפחות אחד' },
  { label: 'שם תלמיד', note: 'לא חובה — לאימות מול המזהה' },
  { label: 'סיבה', note: 'תוצג בהיסטוריית התלמיד' },
];

export default function PointsImportModal({ open, onClose, campId }) {
  const { analyze, commit } = usePointsImport(campId);
  const camp = useCamp(campId);
  const isStar = camp.data?.currency === 'STAR';
  const [step, setStep] = useState('select'); // 'select' | 'review' | 'done'
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);

  function reset() {
    setStep('select');
    setFile(null);
    setBusy(false);
    setError(null);
    setReport(null);
    setResult(null);
  }
  function close() {
    reset();
    onClose();
  }

  async function run(fn, onOk) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      onOk(await fn(file));
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={close} title="טעינת נקודות מאקסל" maxWidth="max-w-2xl">
      {step === 'select' && (
        <SelectStep
          file={file}
          setFile={setFile}
          busy={busy}
          error={error}
          isStar={isStar}
          onCancel={close}
          onNext={() => run(analyze, (r) => { setReport(r); setStep('review'); })}
        />
      )}

      {step === 'review' && report && (
        <ReviewStep
          report={report}
          busy={busy}
          error={error}
          onBack={() => { setError(null); setStep('select'); }}
          onConfirm={() => run(commit, (r) => { setResult(r); setStep('done'); })}
        />
      )}

      {step === 'done' && result && (
        <DoneStep result={result} onClose={close} />
      )}
    </Modal>
  );
}

/* ───────── Step 1: select file ───────── */

function SelectStep({ file, setFile, busy, error, isStar, onCancel, onNext }) {
  return (
    <div className="space-y-5">
      <div className="card-soft p-4">
        <h3 className="text-sm font-bold text-brand-900 mb-2">מבנה הקובץ</h3>
        <ul className="space-y-1.5 text-sm text-ink-700">
          {formatSpec(isStar).map((c) => (
            <li key={c.label} className="flex items-center gap-2 flex-wrap">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
              <span className="font-semibold">{c.label}</span>
              {c.required ? (
                <span className="badge-joy text-[10px]">חובה</span>
              ) : (
                <span className="text-xs text-ink-400">(לא חובה)</span>
              )}
              {c.note && <span className="text-xs text-ink-500">· {c.note}</span>}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-ink-500">
          שורה ראשונה = כותרות. העמודות מזוהות לפי שם הכותרת — הסדר לא משנה.
          כל שורה מקבלת את הנקודות והסיבה שלה. אם תלמיד אחד או יותר לא יזוהה,
          לא תתבצע שום טעינה. עד 2000 שורות, קובץ עד 2MB.
        </p>
      </div>

      <DropZone file={file} setFile={setFile} />

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          ביטול
        </button>
        <button type="button" className="btn-primary" onClick={onNext} disabled={!file || busy}>
          {busy ? 'בודק את הקובץ...' : 'בדיקה לפני טעינה'}
        </button>
      </div>
    </div>
  );
}

/* ───────── Step 2: review (dry-run) ───────── */

const BLOCK_MESSAGE = {
  missing_required: 'חסרה עמודת חובה "נקודות" — הוסיפו אותה לקובץ ונסו שוב.',
  no_identifier_column:
    'חסרה עמודת מזהה — צריך לפחות אחת מתוך: מספר כרטיס / מספר זהות / מזהה חיצוני / מזהה מערכת.',
  duplicate_columns:
    'נמצאו עמודות עם אותה כותרת. לכל עמודה חייבת להיות כותרת ייחודית — תקנו את הקובץ ונסו שוב.',
  empty: 'הקובץ ריק או לא תקין.',
  no_rows: 'לא נמצאו שורות נתונים בקובץ.',
};

function ReviewStep({ report, busy, error, onBack, onConfirm }) {
  const { summary, problems, blocked, blockReason } = report;
  const hasProblems = problems && problems.length > 0;
  const canCommit = !blocked && summary.matched > 0;

  return (
    <div className="space-y-4">
      <ColumnReport columns={report.columns} />

      {blocked && blockReason !== 'has_problems' ? (
        <ErrorBanner>{BLOCK_MESSAGE[blockReason] || 'לא ניתן לבצע את הטעינה.'}</ErrorBanner>
      ) : (
        <SummaryCards summary={summary} mode="preview" />
      )}

      {hasProblems && (
        <>
          <ErrorBanner>
            יש שורות שלא זוהו — לא בוצעה טעינה. תקנו את הקובץ ונסו שוב (פעולה היא הכל-או-כלום).
          </ErrorBanner>
          <ProblemsTable problems={problems} />
        </>
      )}

      {!blocked && report.sample?.length > 0 && (
        <SampleTable sample={report.sample} matched={summary.matched} />
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onBack} disabled={busy}>
          חזרה
        </button>
        <button type="button" className="btn-primary" onClick={onConfirm} disabled={!canCommit || busy}>
          {busy ? 'טוען נקודות...' : `טען נקודות (${summary.matched})`}
        </button>
      </div>
    </div>
  );
}

/* ───────── Step 3: done ───────── */

function DoneStep({ result, onClose }) {
  const { summary } = result;
  return (
    <div className="space-y-4">
      <div className="card p-5 bg-mint-50 border-mint-200 text-mint-900 flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-mint-500 text-white grid place-items-center shrink-0">
          <CheckIcon className="w-6 h-6" />
        </div>
        <div>
          <strong className="block text-lg">הטעינה הושלמה</strong>
          <span className="text-sm num">
            נטענו {formatNumber(summary.totalPoints)} נקודות ל-{summary.applied} תלמידים
          </span>
        </div>
      </div>

      <SummaryCards summary={summary} mode="done" />
      {result.sample?.length > 0 && <SampleTable sample={result.sample} matched={summary.applied} />}

      <div className="flex justify-end pt-1">
        <button type="button" className="btn-primary" onClick={onClose}>
          סגור
        </button>
      </div>
    </div>
  );
}

/* ───────── Column report ───────── */

function ColumnReport({ columns }) {
  const { recognized = [], missingRequired = [], ignored = [], duplicates = [] } = columns;
  // Only identifiers + points/reason are interesting here; show the recognized
  // set and any unknown headers so the manager knows what was picked up.
  return (
    <div className="space-y-2.5">
      {duplicates.length > 0 && (
        <ReportLine tone="danger" Icon={ExclamationTriangleIcon} title="עמודות עם כותרת כפולה">
          {duplicates.map((d) => `${d.header} (×${d.count})`).join(' · ')}
        </ReportLine>
      )}
      {missingRequired.length > 0 && (
        <ReportLine tone="danger" Icon={ExclamationTriangleIcon} title="עמודות חובה חסרות">
          {missingRequired.map((c) => c.label).join(' · ')}
        </ReportLine>
      )}
      {recognized.length > 0 && (
        <ReportLine tone="ok" Icon={CheckIcon} title={`עמודות מזוהות (${recognized.length})`}>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {recognized.map((c) => (
              <span key={c.key} className="badge bg-mint-100 text-mint-800 border border-mint-200">
                {c.label}
              </span>
            ))}
          </div>
        </ReportLine>
      )}
      {ignored.length > 0 && (
        <ReportLine tone="muted" Icon={InfoIcon} title={`עמודות בקובץ שלא יטופלו (${ignored.length})`}>
          {ignored.map((c) => c.header).join(' · ')}
        </ReportLine>
      )}
    </div>
  );
}

const REPORT_TONE = {
  ok: 'bg-mint-50 border-mint-200 text-mint-900',
  danger: 'bg-joy-50 border-joy-200 text-joy-900',
  muted: 'bg-ink-50 border-ink-200 text-ink-600',
};

function ReportLine({ tone, Icon, title, children }) {
  return (
    <div className={`rounded-2xl border px-4 py-2.5 text-sm ${REPORT_TONE[tone]}`}>
      <div className="flex items-center gap-2 font-bold">
        <Icon className="w-4 h-4 shrink-0" />
        <span>{title}</span>
      </div>
      <div className="mt-0.5 ms-6">{children}</div>
    </div>
  );
}

/* ───────── Summary cards ───────── */

function SummaryCards({ summary, mode }) {
  const preview = mode === 'preview';
  const cards = [
    { label: 'סה״כ שורות', value: summary.total, tone: 'ink' },
    { label: preview ? 'יזוהו' : 'זוהו', value: summary.matched, tone: 'mint' },
    {
      label: preview ? 'נקודות לטעינה' : 'נקודות שנטענו',
      value: summary.totalPoints,
      tone: 'sky',
    },
    { label: 'בעיות', value: summary.problems, tone: summary.problems > 0 ? 'joy' : 'ink' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {cards.map((c) => (
        <div key={c.label} className={`rounded-2xl border p-3 text-center ${STAT_TONE[c.tone]}`}>
          <div className="text-2xl font-extrabold num">{formatNumber(Number(c.value))}</div>
          <div className="text-[11px] font-bold mt-0.5">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

const STAT_TONE = {
  ink: 'bg-white border-ink-200 text-ink-800',
  mint: 'bg-mint-50 border-mint-200 text-mint-800',
  sky: 'bg-sky-50 border-sky-200 text-sky-800',
  joy: 'bg-joy-50 border-joy-200 text-joy-800',
};

/* ───────── Matched sample ───────── */

function SampleTable({ sample, matched }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 bg-mint-50 border-b border-mint-200 text-sm font-bold text-mint-900 num">
        תצוגה מקדימה{matched > sample.length ? ` (${sample.length} מתוך ${matched})` : ''}
      </div>
      <ul className="divide-y divide-ink-100 max-h-64 overflow-y-auto">
        {sample.map((s) => (
          <li key={s.row} className="px-4 py-2.5 text-sm flex items-center gap-3">
            <span className="flex-1 min-w-0 truncate font-semibold text-ink-900">
              {s.name}
              {s.className && <span className="badge-indigo ms-2">{s.className}</span>}
            </span>
            <span className="text-xs text-ink-500 truncate max-w-[12rem]">{s.reason}</span>
            <span className="num font-bold text-mint-700 shrink-0">+{formatNumber(s.points)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ───────── Problem rows ───────── */

function ProblemsTable({ problems }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 bg-joy-50 border-b border-joy-200 text-sm font-bold text-joy-900 num">
        שורות לא מזוהות ({problems.length})
      </div>
      <ul className="divide-y divide-ink-100 max-h-64 overflow-y-auto">
        {problems.map((p) => (
          <li key={p.row} className="px-4 py-2.5 text-sm">
            <span className="font-semibold text-ink-900 num">שורה {p.row}</span>
            {p.identifier && p.identifier !== '—' && (
              <span className="text-xs text-ink-500 ms-2">({p.identifier})</span>
            )}
            <ul className="ms-3 mt-0.5 list-disc list-inside text-xs text-joy-700">
              {p.errors.map((e, idx) => (
                <li key={idx}>{e}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ───────── Bits ───────── */

function DropZone({ file, setFile }) {
  // Wire drag-and-drop (advertised but previously click-only) + preventDefault
  // so the browser doesn't open the dropped file.
  const [dragging, setDragging] = useState(false);
  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  }
  return (
    <label
      onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
      onDrop={onDrop}
      className={`block rounded-2xl border-2 border-dashed transition-colors cursor-pointer
        ${dragging ? 'border-brand-500 bg-brand-50'
          : file ? 'border-mint-400 bg-mint-50'
          : 'border-ink-300 hover:border-brand-400 hover:bg-brand-50/40'}`}
    >
      <input
        type="file"
        accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="sr-only"
      />
      <div className="p-6 text-center">
        {file ? (
          <>
            <div className="mx-auto w-12 h-12 rounded-2xl bg-mint-100 text-mint-700 grid place-items-center mb-2">
              <FileIcon className="w-6 h-6" />
            </div>
            <div className="text-sm font-semibold text-ink-900">{file.name}</div>
            <div className="text-xs text-ink-500 num mt-0.5">
              {Math.round(file.size / 1024)} KB · לחץ להחלפת קובץ
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto w-12 h-12 rounded-2xl bg-brand-100 text-brand-700 grid place-items-center mb-2">
              <BanknotesIcon className="w-6 h-6" />
            </div>
            <div className="text-sm font-semibold text-ink-900">בחר קובץ XLSX</div>
            <div className="text-xs text-ink-500 mt-0.5">או גרור לכאן</div>
          </>
        )}
      </div>
    </label>
  );
}

function ErrorBanner({ children }) {
  return (
    <div className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2">
      <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

function humanizeError(e) {
  const code = e?.code || e?.message || '';
  if (e?.status === 413 || /too_large/.test(code)) return 'הקובץ גדול מדי (עד 2MB / 2000 שורות).';
  if (/xlsx_empty/.test(code)) return 'הקובץ ריק או לא תקין.';
  if (e?.status === 403) return 'אין לך הרשאה לפעולה זו.';
  if (e?.status === 401) return 'הסשן פג. רענן את הדף והיכנס מחדש.';
  if (e?.status >= 500) return `שגיאת שרת (${e.status}). נסה שוב בעוד רגע.`;
  return `שגיאה: ${e?.message || e?.code || 'לא צפויה'}`;
}

function CheckIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}
function FileIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}
function InfoIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  );
}
